'use client';

import { Badge } from '@/components/badge';
import { BrandLink } from '@/components/brand-link';
import { DiscountBadge } from '@/components/discount-badge';
import { buttonVariants } from '@/components/ui/button';
import { WishlistButton } from '@/components/wishlist-button-product';
import type { ProductCardProps } from '@/lib/types';
import { serializeProductCardToElevarProduct } from '@/lib/utils';
import { siteConfig } from '@/site-config';
import { BadgeOverlayContainer } from '@veranda/shared/components/badge-overlay-container';
import { ProductCard as SharedProductCard } from '@veranda/shared/components/product-card';
import type { ElevarImpression } from '@veranda/shared/elevar';
import { useElevarEvents } from '@veranda/shared/elevar';
import { OkendoStarRating } from '@veranda/shared/reviews';
import { usePathname } from 'next/navigation';

export function ProductCard({
  product,
  index,
  className,
  elevarImpression,
}: {
  product: ProductCardProps;
  index: number;
  className?: string;
  elevarImpression?: ElevarImpression;
}) {
  const { trackSelectItem } = useElevarEvents();
  const pathname = usePathname();

  function handleSelectItem() {
    trackSelectItem({
      list: pathname,
      products: [
        serializeProductCardToElevarProduct({
          product,
          position: index + 1,
          list: pathname,
        }),
      ],
    });
  }

  return (
    <SharedProductCard
      product={product}
      index={index}
      onSelectItem={handleSelectItem}
      className={className}
      elevarImpression={elevarImpression}
    >
      <SharedProductCard.ImageContainer product={product}>
        <SharedProductCard.Inventory productGid={product.productGid}>
          {({ isOutOfStock }: { isOutOfStock: boolean }) => (
            <BadgeOverlayContainer>
              {isOutOfStock && <Badge>Sold out</Badge>}
              {product.showDiscountBadges && (
                <DiscountBadge
                  price={product.price ?? 0}
                  compareAtPrice={product.compareAtPrice ?? 0}
                />
              )}
            </BadgeOverlayContainer>
          )}
        </SharedProductCard.Inventory>
      </SharedProductCard.ImageContainer>
      {product.productGid && product.variantGid && (
        <WishlistButton
          productGid={product.productGid}
          variantGid={product.variantGid}
        />
      )}
      <SharedProductCard.Content>
        <SharedProductCard.Header>
          <SharedProductCard.Title product={product} className="text-primary" />
          {product.brand?.name && product.brand?.link?.slug && (
            <BrandLink
              name={product.brand.name}
              slug={product.brand.link.slug}
              asSpan={true}
            />
          )}
        </SharedProductCard.Header>
        <SharedProductCard.Footer>
          {product.productGid && (
            <OkendoStarRating productGid={product.productGid} />
          )}
          <SharedProductCard.PriceRow product={product} />
          <SharedProductCard.AddToCartButton
            product={product}
            domain={siteConfig.baseUrl}
            buttonClassName={buttonVariants()}
          />
        </SharedProductCard.Footer>
      </SharedProductCard.Content>
    </SharedProductCard>
  );
}
